/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SipEndpointsPage.tsx
 🗓️ Created: 2025-12-23
 🧩 Description:
    SIP Endpoints page - displays active SIP registrations from FreeSWITCH.
    Uses the standard DataGridSimple layout consistent with other modules.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
 ────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { RefreshCw, Wifi, WifiOff, Clock } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { Badge } from '../../components/ui/badge';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { fetchSipEndpoints, refreshSipEndpoints, SipRegistration } from './api';

interface SipEndpointColumn {
    key: string;
    label: string;
    sortable?: boolean;
    visible: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: any) => React.ReactNode;
}

export default function SipEndpointsPage() {
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    // Effective domain ID for contextual filtering
    const effectiveDomainId = selectedDomain?.id;

    const [registrations, setRegistrations] = useState<SipRegistration[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortBy, setSortBy] = useState('reg_user');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Format expires time
    const formatExpires = useCallback((expires: number) => {
        const now = Math.floor(Date.now() / 1000);
        const diff = expires - now;

        if (diff < 0) return t('sipEndpoints.expired');

        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`;
        }
        return `${seconds}s`;
    }, [t]);

    // Define columns
    const [columns, setColumns] = useState<SipEndpointColumn[]>([]);

    const availableColumns = useMemo<SipEndpointColumn[]>(() => [
        {
            key: 'status',
            label: t('sipEndpoints.columns.status'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <Badge
                    variant={item.status === 'registered' ? 'default' : 'destructive'}
                    className={`flex items-center gap-1 w-fit ${item.status === 'registered' ? 'bg-green-500 hover:bg-green-600' : ''}`}
                >
                    {item.status === 'registered' ? (
                        <Wifi className="h-3 w-3" />
                    ) : (
                        <WifiOff className="h-3 w-3" />
                    )}
                    {item.status === 'registered' ? t('sipEndpoints.registered') : t('sipEndpoints.expired')}
                </Badge>
            )
        },
        {
            key: 'reg_user',
            label: t('sipEndpoints.columns.extension'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="font-mono font-semibold text-primary">{item.reg_user}</span>
            )
        },
        {
            key: 'network_ip',
            label: t('sipEndpoints.columns.ipAddress'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="font-mono text-sm">{item.network_ip}</span>
            )
        },
        {
            key: 'network_port',
            label: t('sipEndpoints.columns.port'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="font-mono text-sm">{item.network_port}</span>
            )
        },
        {
            key: 'network_proto',
            label: t('sipEndpoints.columns.protocol'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <Badge variant="outline" className="uppercase">
                    {item.network_proto}
                </Badge>
            )
        },
        {
            key: 'realm',
            label: t('sipEndpoints.columns.realm'),
            sortable: false,
            visible: false,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="text-sm text-muted-foreground">{item.realm}</span>
            )
        },
        {
            key: 'hostname',
            label: t('sipEndpoints.columns.hostname'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="text-sm">{item.hostname}</span>
            )
        },
        {
            key: 'expires',
            label: t('sipEndpoints.columns.expiresIn'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="flex items-center gap-1 text-sm">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    {formatExpires(item.expires)}
                </span>
            )
        },
        {
            key: 'url',
            label: t('sipEndpoints.columns.sipUrl'),
            sortable: false,
            visible: false,
            render: (_value: unknown, item: SipRegistration) => (
                <span className="font-mono text-xs text-muted-foreground truncate max-w-[200px] block" title={item.url}>
                    {item.url}
                </span>
            )
        }
    ], [t, i18n.language, formatExpires]);

    // Initialize columns on mount
    useEffect(() => {
        setColumns(availableColumns);
    }, [availableColumns]);

    // Load data
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const result = await fetchSipEndpoints({ page, limit, search: searchQuery, domainId: effectiveDomainId });

            setRegistrations(result.data);
            setTotal(result.pagination?.total || 0);
            setTotalPages(result.pagination?.totalPages || 0);
        } catch (error) {
            showError(error.message || t('sipEndpoints.messages.loadError'));
        } finally {
            setLoading(false);
        }
    }, [page, limit, searchQuery, effectiveDomainId, showError, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handlers
    const handleRefresh = useCallback(async () => {
        try {
            setRefreshing(true);
            await refreshSipEndpoints();
            await loadData();
            showSuccess(t('sipEndpoints.messages.refreshSuccess'));
        } catch (error) {
            showError(error.message || t('sipEndpoints.messages.refreshError'));
        } finally {
            setRefreshing(false);
        }
    }, [loadData, showSuccess, showError, t]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            setSortOrder(current => current === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(current =>
            current.map(col =>
                col.key === key ? { ...col, visible: !col.visible } : col
            )
        );
    }, []);

    // Pagination object for DataGridSimple
    const pagination = useMemo(() => ({
        page,
        limit,
        total,
        totalPages
    }), [page, limit, total, totalPages]);

    return (
        <div className="p-6">
            {/* Data Grid */}
            <DataGridSimple
                data={registrations}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                searchPlaceholder={t('sipEndpoints.searchPlaceholder')}
                title={t('sipEndpoints.title')}
                titleIcon={<Wifi className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('sipEndpoints.refresh')} position="top">
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="p-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />
        </div>
    );
}
